import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../../lib/api";
import { PageHeader, Loading, EmptyState, inr } from "../../components/shared";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../components/ui/dialog";
import { toast } from "sonner";
import { IndianRupee, Ticket, Settings2, Plus } from "lucide-react";

const TONE = {
  active: "bg-secondary/15 text-secondary", promotional: "bg-secondary/15 text-secondary",
  trial: "bg-amber-100 text-amber-700", expired: "bg-rose-100 text-rose-700",
  waived: "bg-primary/10 text-primary", suspended: "bg-muted text-muted-foreground",
};
const Badge = ({ s }) => <span className={`text-xs font-semibold px-2 py-0.5 rounded-full capitalize ${TONE[s] || "bg-muted"}`} data-testid={`status-${s}`}>{s}</span>;

export default function AdminSubscriptions() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [socs, setSocs] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [manage, setManage] = useState(null);
  const [vmanage, setVManage] = useState(null);
  const [cForm, setCForm] = useState({ code: "", type: "percent", value: 10, applies_to: "society" });

  const load = () => {
    api.get("/admin/subscriptions").then((r) => setSocs(r.data)).catch(() => setSocs([]));
    api.get("/admin/subscriptions/vendors").then((r) => setVendors(r.data)).catch(() => {});
    api.get("/admin/coupons").then((r) => setCoupons(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const act = async (id, path, body) => {
    try { await api.post(`/admin/subscriptions/${id}/${path}`, body || {}); toast.success("Updated"); load();
      setManage((m) => m && socs.find((x) => x.society_id === id)); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const vact = async (id, action, body) => {
    try { await api.post(`/admin/subscriptions/vendors/${id}/${action}`, body || {}); toast.success("Updated"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const createCoupon = async () => {
    try { await api.post("/admin/coupons", cForm); toast.success("Coupon created"); setCForm({ code: "", type: "percent", value: 10, applies_to: "society" }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (!socs) return <Loading />;

  return (
    <div>
      <PageHeader title="Subscriptions" subtitle="Manage society ERP, resident add-on and vendor subscriptions." />
      <Tabs onValueChange={(v) => setSearchParams(v === "societies" ? {} : { tab: v })} value={["societies","vendors","coupons"].includes(searchParams.get("tab")) ? searchParams.get("tab") : "societies"}>
        <TabsList className="mb-5">
          <TabsTrigger value="societies" data-testid="sub-tab-societies">Societies ({socs.length})</TabsTrigger>
          <TabsTrigger value="vendors" data-testid="sub-tab-vendors">Vendors ({vendors.length})</TabsTrigger>
          <TabsTrigger value="coupons" data-testid="sub-tab-coupons">Coupons ({coupons.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="societies">
          <div className="bg-card border rounded-xl overflow-x-auto">
            <Table>
              <TableHeader><TableRow><TableHead>Society</TableHead><TableHead>Members</TableHead><TableHead>Monthly Fee</TableHead><TableHead>Status</TableHead><TableHead>Next Renewal</TableHead><TableHead></TableHead></TableRow></TableHeader>
              <TableBody>
                {socs.map((r) => (
                  <TableRow key={r.society_id} data-testid={`sub-row-${r.society_id}`}>
                    <TableCell className="font-medium">{r.society_name}</TableCell>
                    <TableCell>{r.members}</TableCell>
                    <TableCell className="font-semibold">{inr(r.total_monthly)}{r.discount_pct > 0 && <span className="text-xs text-secondary ml-1">-{r.discount_pct}%</span>}</TableCell>
                    <TableCell><Badge s={r.effective_status} /></TableCell>
                    <TableCell className="text-sm text-muted-foreground">{(r.next_renewal || "").slice(0, 10) || "—"}</TableCell>
                    <TableCell><Button size="sm" variant="outline" className="rounded-full gap-1.5 h-8" onClick={() => setManage(r)} data-testid={`sub-manage-${r.society_id}`}><Settings2 className="w-3.5 h-3.5" /> Manage</Button></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="vendors">
          <div className="bg-card border rounded-xl overflow-x-auto">
            <Table>
              <TableHeader><TableRow><TableHead>Vendor</TableHead><TableHead>Monthly</TableHead><TableHead>Status</TableHead><TableHead>Next Renewal</TableHead><TableHead></TableHead></TableRow></TableHeader>
              <TableBody>
                {vendors.map((v) => (
                  <TableRow key={v.vendor_id} data-testid={`vsub-row-${v.vendor_id}`}>
                    <TableCell className="font-medium">{v.business_name}</TableCell>
                    <TableCell>{inr(v.monthly_amount)}</TableCell>
                    <TableCell><Badge s={v.effective_status} /></TableCell>
                    <TableCell className="text-sm text-muted-foreground">{(v.next_renewal || "").slice(0, 10) || "—"}</TableCell>
                    <TableCell><Button size="sm" variant="outline" className="rounded-full h-8" onClick={() => setVManage(v)} data-testid={`vsub-manage-${v.vendor_id}`}>Manage</Button></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="coupons">
          <div className="bg-card border rounded-xl p-5 mb-4">
            <h3 className="font-semibold mb-3 flex items-center gap-2"><Ticket className="w-4 h-4" /> Create Coupon</h3>
            <div className="grid sm:grid-cols-5 gap-3 items-end">
              <div><Label className="text-xs mb-1 block">Code</Label><Input value={cForm.code} onChange={(e) => setCForm({ ...cForm, code: e.target.value.toUpperCase() })} placeholder="WELCOME10" data-testid="coupon-code" /></div>
              <div><Label className="text-xs mb-1 block">Type</Label><Select value={cForm.type} onValueChange={(v) => setCForm({ ...cForm, type: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="percent">Percent %</SelectItem><SelectItem value="flat">Flat ₹</SelectItem></SelectContent></Select></div>
              <div><Label className="text-xs mb-1 block">Value</Label><Input type="number" value={cForm.value} onChange={(e) => setCForm({ ...cForm, value: Number(e.target.value) })} /></div>
              <div><Label className="text-xs mb-1 block">Applies to</Label><Select value={cForm.applies_to} onValueChange={(v) => setCForm({ ...cForm, applies_to: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="society">Society</SelectItem><SelectItem value="vendor">Vendor</SelectItem><SelectItem value="resident">Resident</SelectItem><SelectItem value="all">All</SelectItem></SelectContent></Select></div>
              <Button onClick={createCoupon} className="rounded-full gap-1.5" data-testid="coupon-create"><Plus className="w-4 h-4" /> Create</Button>
            </div>
          </div>
          {coupons.length === 0 ? <EmptyState icon={Ticket} title="No coupons yet" /> : (
            <div className="bg-card border rounded-xl overflow-x-auto"><Table>
              <TableHeader><TableRow><TableHead>Code</TableHead><TableHead>Type</TableHead><TableHead>Value</TableHead><TableHead>Applies</TableHead><TableHead>Used</TableHead><TableHead>Active</TableHead></TableRow></TableHeader>
              <TableBody>{coupons.map((c) => (
                <TableRow key={c.coupon_id}><TableCell className="font-mono font-medium">{c.code}</TableCell><TableCell>{c.type}</TableCell><TableCell>{c.type === "percent" ? `${c.value}%` : inr(c.value)}</TableCell><TableCell className="capitalize">{c.applies_to}</TableCell><TableCell>{c.used}</TableCell>
                  <TableCell><Switch checked={c.active} onCheckedChange={(v) => api.put(`/admin/coupons/${c.coupon_id}`, { active: v }).then(load)} /></TableCell></TableRow>
              ))}</TableBody>
            </Table></div>
          )}
        </TabsContent>
      </Tabs>

      {/* Society manage dialog */}
      <Dialog open={!!manage} onOpenChange={(o) => !o && setManage(null)}>
        <DialogContent data-testid="sub-manage-dialog">
          <DialogHeader><DialogTitle className="font-display">{manage?.society_name}</DialogTitle><DialogDescription>{manage?.members} members · fee {inr(manage?.total_monthly || 0)}/mo · <Badge s={manage?.effective_status} /></DialogDescription></DialogHeader>
          {manage && <div className="space-y-3">
            <div className="flex items-center justify-between border rounded-lg px-3 py-2"><span className="text-sm">Resident Portal add-on</span><Switch checked={manage.resident_portal_enabled} onCheckedChange={(v) => act(manage.society_id, "resident-portal", { enabled: v })} data-testid="mng-resident" /></div>
            <div className="grid grid-cols-2 gap-2">
              <Button variant="outline" className="rounded-lg" onClick={() => act(manage.society_id, "activate", { plan: "monthly" })} data-testid="mng-activate-monthly">Activate Monthly</Button>
              <Button variant="outline" className="rounded-lg" onClick={() => act(manage.society_id, "activate", { plan: "annual" })} data-testid="mng-activate-annual">Activate Annual</Button>
              <Button variant="outline" className="rounded-lg" onClick={() => act(manage.society_id, "waive", { period: "6m", reason: "Admin waiver" })} data-testid="mng-waive">Waive 6 months</Button>
              <Button variant="outline" className="rounded-lg" onClick={() => act(manage.society_id, "trial", { days: 30 })} data-testid="mng-trial">Extend Trial 30d</Button>
              <Button variant="outline" className="rounded-lg" onClick={() => act(manage.society_id, "extend", { days: 30 })} data-testid="mng-extend">Extend 30 days</Button>
              <Button variant="outline" className="rounded-lg" onClick={() => act(manage.society_id, "discount", { discount_pct: 10 })} data-testid="mng-discount">Apply 10% discount</Button>
              <Button variant="outline" className="rounded-lg" onClick={() => act(manage.society_id, "invoice")} data-testid="mng-invoice">Generate Invoice</Button>
              <Button variant="outline" className="rounded-lg text-rose-600" onClick={() => act(manage.society_id, "suspend")} data-testid="mng-suspend">Suspend</Button>
            </div>
          </div>}
        </DialogContent>
      </Dialog>

      {/* Vendor manage dialog */}
      <Dialog open={!!vmanage} onOpenChange={(o) => !o && setVManage(null)}>
        <DialogContent data-testid="vsub-manage-dialog">
          <DialogHeader><DialogTitle className="font-display">{vmanage?.business_name}</DialogTitle><DialogDescription>{inr(vmanage?.monthly_amount || 0)}/mo · <Badge s={vmanage?.effective_status} /></DialogDescription></DialogHeader>
          {vmanage && <div className="grid grid-cols-2 gap-2">
            <Button variant="outline" onClick={() => { vact(vmanage.vendor_id, "activate", { plan: "monthly" }); setVManage(null); }} data-testid="vmng-activate">Activate Monthly</Button>
            <Button variant="outline" onClick={() => { vact(vmanage.vendor_id, "waive", { period: "6m", reason: "Strategic partner" }); setVManage(null); }} data-testid="vmng-waive">Waive 6 months</Button>
            <Button variant="outline" onClick={() => { vact(vmanage.vendor_id, "extend", { days: 30 }); setVManage(null); }}>Extend 30 days</Button>
            <Button variant="outline" className="text-rose-600" onClick={() => { vact(vmanage.vendor_id, "suspend"); setVManage(null); }}>Suspend</Button>
          </div>}
        </DialogContent>
      </Dialog>
    </div>
  );
}
